import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import { OutlinedIcons, ZAvatar } from '@marageti/z4-lib';
import { TicketComment } from '@marageti/z4-sdk/lib/tickets';

const { OutlinedPencilIcon, OutlinedUploadIcon } = OutlinedIcons;

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

type TicketCommentItemProps = {
  comment: TicketComment;
  currentUserId?: string;
  onEdit?: (comment: TicketComment) => void;
  onDownloadFile?: (comment: TicketComment) => void;
};

const isEdited = (c: TicketComment): boolean => {
  if (!c.updatedAt || !c.createdAt) return false;
  return c.updatedAt !== c.createdAt;
};

const TicketCommentItem = ({
  comment,
  currentUserId,
  onEdit,
  onDownloadFile,
}: TicketCommentItemProps) => {
  const isOwner = currentUserId === comment.createdBy;
  const isSystemEntry = comment.type === 'LOG' || comment.type === 'CLOSE';

  if (isSystemEntry) {
    return (
      <Box sx={{ py: 2.5, px: 2, textAlign: 'center' }}>
        <Typography
          variant="body"
          color="text.primary"
          component="div"
          sx={{
            '& p': { m: 0 },
            '& strong': { fontWeight: 700 },
            '& small': { color: 'text.secondary' },
            '& i': {
              fontStyle: 'italic',
              color: 'text.secondary',
            },
          }}
          dangerouslySetInnerHTML={{ __html: comment.text }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {formatTimestamp(comment.createdAt)}
        </Typography>
      </Box>
    );
  }

  if (comment.type === 'FILE') {
    return (
      <Box sx={{ py: 2.5, px: 2, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <OutlinedUploadIcon sx={{ width: 20, height: 20, color: 'white' }} />
          </Box>
        </Box>
        <Typography variant="body" component="div">
          {comment.createdByName}
          {' uploaded '}
          <Box
            component="button"
            sx={{
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'none' },
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              fontSize: 'inherit',
            }}
            onClick={() => onDownloadFile?.(comment)}
          >
            {comment.fileName || 'attachment'}
          </Box>
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {formatTimestamp(comment.createdAt)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        my: 0.5,
        '&:hover .comment-edit-btn': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          py: 3,
          px: 3,
          mt: 1,
          backgroundColor: 'common.gold10',
          borderRadius: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <ZAvatar name={comment.createdByName} size={32} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="bodyBold">
                {comment.createdByName}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.disabled">
                  {formatTimestamp(comment.createdAt)}
                </Typography>
                {isEdited(comment) && (
                  <Typography variant="caption" color="text.disabled">
                    • Edited
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Typography
              variant="body"
              component="div"
              sx={{
                '& p': { mt: 0, mb: 1 },
                '& p:last-child': { mb: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: comment.text }}
            />
          </Box>
        </Stack>
      </Box>

      {isOwner && onEdit && (
        <Box sx={{
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
        >
          <Tooltip title="Edit" placement="top" slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] } }}>
            <IconButton
              className="comment-edit-btn"
              onClick={() => onEdit(comment)}
              sx={{
                opacity: 0,
                transition: 'opacity 0.15s',
                backgroundColor: 'common.white',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'background.paper' },
                width: 40,
                height: 40,
              }}
            >
              <OutlinedPencilIcon sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default TicketCommentItem;
